import React, { useState } from 'react';
import {
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { TimezonePreferenceType } from '@/types/timezone';

dayjs.extend(timezone);

interface TimezonePreferenceProps {
  onSave?: (preference: TimezonePreferenceType, timezone: string | null) => void;
}

export const TimezonePreference: React.FC<TimezonePreferenceProps> = ({ onSave }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const orgSettings = useSelector((state: RootState) => state.organization.settings);

  const [preference, setPreference] = useState<TimezonePreferenceType>(
    user?.timezonePreference || 'system'
  );
  const [selectedTimezone, setSelectedTimezone] = useState(
    user?.timezone || dayjs.tz.guess()
  );

  const currentSystemTimezone = dayjs.tz.guess();
  const orgTimezone = orgSettings?.timezone || 'America/New_York';

  // Get user-friendly timezone names
  const getTimezoneName = (tz: string) => {
    try {
      // Use short name to get abbreviations like EST/PST which are more familiar
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: tz,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(new Date());
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
      
      // Convert abbreviations to more user-friendly names
      const friendlyNames: Record<string, string> = {
        'EST': 'Eastern Time',
        'EDT': 'Eastern Time', 
        'CST': 'Central Time',
        'CDT': 'Central Time',
        'MST': 'Mountain Time',
        'MDT': 'Mountain Time',
        'PST': 'Pacific Time',
        'PDT': 'Pacific Time',
        'AKST': 'Alaska Time',
        'AKDT': 'Alaska Time',
        'GMT': 'Greenwich Mean Time',
        'BST': 'British Time',
        'CET': 'Central European Time',
        'CEST': 'Central European Time',
        'JST': 'Japan Time',
        'AEDT': 'Australian Eastern Time',
        'AEST': 'Australian Eastern Time',
        'HST': 'Hawaii Time'
      };
      
      // Handle GMT+X cases and timezone ID fallbacks
      if (timeZoneName && friendlyNames[timeZoneName]) {
        return friendlyNames[timeZoneName];
      } else if (timeZoneName?.startsWith('GMT')) {
        // For GMT+X cases, use the timezone ID to get a better name
        const cityNames: Record<string, string> = {
          'Asia/Tokyo': 'Japan Time',
          'Asia/Shanghai': 'China Time',
          'Asia/Kolkata': 'India Time',
          'Australia/Sydney': 'Australian Eastern Time',
          'Australia/Melbourne': 'Australian Eastern Time',
          'Europe/Berlin': 'Central European Time',
          'Europe/Rome': 'Central European Time',
          'America/Phoenix': 'Arizona Time'
        };
        return cityNames[tz] || tz.split('/')[1]?.replace(/_/g, ' ') + ' Time' || tz;
      }
      
      return timeZoneName || tz.split('/')[1]?.replace(/_/g, ' ') + ' Time' || tz;
    } catch {
      return tz.split('/')[1]?.replace(/_/g, ' ') + ' Time' || tz;
    }
  };

  // Get common timezones dynamically from dayjs
  const getCommonTimezones = () => {
    try {
      // Get timezone names from dayjs - it has access to IANA timezone database
      const commonRegions = [
        'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
        'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
        'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata',
        'Australia/Sydney', 'Australia/Melbourne'
      ];

      // Filter to only valid timezones that dayjs supports
      const validTimezones = commonRegions.filter((tz: string) => {
        try {
          dayjs().tz(tz);
          return true;
        } catch {
          return false;
        }
      });

      return validTimezones.map((tz: string) => ({
        value: tz,
        name: getTimezoneName(tz),
        location: tz.replace('_', ' ')
      })).sort((a: any, b: any) => a.name.localeCompare(b.name));
      
    } catch {
      // Minimal fallback
      return [
        { value: 'America/New_York', name: 'Eastern Standard Time', location: 'America/New York' },
        { value: 'America/Chicago', name: 'Central Standard Time', location: 'America/Chicago' },
        { value: 'America/Los_Angeles', name: 'Pacific Standard Time', location: 'America/Los Angeles' },
      ];
    }
  };

  const commonTimezones = getCommonTimezones();

  const handlePreferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPreference = event.target.value as TimezonePreferenceType;
    setPreference(newPreference);
    
    if (onSave) {
      onSave(newPreference, newPreference === 'custom' ? selectedTimezone : null);
    }
  };

  const handleTimezoneChange = (event: SelectChangeEvent<string>) => {
    const tz = event.target.value;
    setSelectedTimezone(tz);
    
    if (onSave && preference === 'custom') {
      onSave('custom', tz);
    }
  };

  return (
    <Box>
      <FormControl component="fieldset">
        <RadioGroup
          value={preference}
          onChange={handlePreferenceChange}
        >
          <FormControlLabel
            value="system"
            control={<Radio />}
            label={`System timezone - ${getTimezoneName(currentSystemTimezone)}`}
          />
          <FormHelperText sx={{ ml: 4, mt: -1, mb: 1 }}>
            Automatically uses your device's timezone
          </FormHelperText>

          <FormControlLabel
            value="organization"
            control={<Radio />}
            label={`Organization default - ${getTimezoneName(orgTimezone)}`}
          />
          <FormHelperText sx={{ ml: 4, mt: -1, mb: 1 }}>
            Uses your organization's default timezone
          </FormHelperText>

          <FormControlLabel
            value="custom"
            control={<Radio />}
            label="Custom timezone"
          />
          <FormHelperText sx={{ ml: 4, mt: -1, mb: 2 }}>
            Select a specific timezone below
          </FormHelperText>
        </RadioGroup>
      </FormControl>

      <FormControl fullWidth disabled={preference !== 'custom'} sx={{ mt: 2 }}>
        <InputLabel>Timezone</InputLabel>
        <Select
          value={selectedTimezone}
          onChange={handleTimezoneChange}
          label="Timezone"
        >
          {commonTimezones.map((tz) => (
            <MenuItem key={tz.value} value={tz.value}>
              {tz.name} - {tz.location}
            </MenuItem>
          ))}
        </Select>
        {preference === 'custom' && (
          <FormHelperText>
            Times will always display in this timezone
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
